import './ReferenceInput.css';

import { Popover } from 'react-tiny-popover';
import { IonIcon } from '@ionic/react';
import { menuOutline } from 'ionicons/icons';
import {
  Dispatch,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Chips, ChipsRemoveEvent } from 'primereact/chips';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ApiMethod,
  Dataset,
  Form,
  ReferenceControl,
  ReferenceItem,
  ReferenceValue,
} from '../../../../../types/types';
import type { RequestDataValue } from '../../../../../context/RequestDataContext';
import { getHandlerApiMethods } from '../../../../../utils/handlers/getHandlerApiMethods';
import { resolveControlFilter } from './utils/resolveControlFilter';
import { getPopoverItemRows } from './utils/getPopoverItemRows';
import type { GridInputBaseProps } from '../../types';
import Button from '../../../../Button/Button';
import ReferencePopover from './components/ReferencePopover/ReferencePopover';
import { useDataset } from '../../../../../api/hooks/useDataset';
import { GRID_DATA_LIMIT } from '../../../../../constants';
import useThrottle from '../../../../../hooks/useThrottle';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import {
  getMultisortFromDataset,
  isEmptyValue,
} from '../../../../../utils/managerUtils';
import useChipsScrollbarDetection from '../../../../../hooks/useChipsScrollbarDetection';
import { useDebouncedEffect } from '../../../../../hooks/useDebouncedEffect';
import { getReferenceParamsString } from './utils/getReferenceParamsString';
import { onMultiSelectWrapperMouseDown as handleMultiSelectWrapperMouseDown } from './utils/onMultiSelectWrapperMouseDown';
import { focusMultiSelectInputToEndHandler } from './utils/focusMultiSelectInputToEndHandler';
import { getSingleSelectSearchValue } from './utils/getSingleSelectSearchValue';
import { isOpenOnFocusSuppressed } from '../../../../../utils/forms/openOnFocusSuppression';
import { getSelectedItemsFromValue } from './utils/getSelectedItemsFromValue';
import { getAdditionalSelectedItems } from './utils/getAdditionalSelectedItems';
import { mergeSelectedItems } from './utils/mergeSelectedItems';
import { getRemovedChipIndex } from './utils/getRemovedChipIndex';
import { getTypeToSelectMatch } from './utils/getTypeToSelectMatch';
import { useChipsArrowNavigation } from './hooks/useChipsArrowNavigation';
import { getReferenceLabelFormatter } from './utils/getReferenceLabelFormatter';
import { isDateTimeReferenceColumn } from './utils/isDateTimeReferenceColumn';
import { isReferenceLabelLookup } from './utils/isReferenceLabelLookup';
import { isResolvingDisplayedLabel as getIsResolvingDisplayedLabel } from './utils/isResolvingDisplayedLabel';
import { isResolvingLabel as getIsResolvingLabel } from './utils/isResolvingLabel';
import { isFocusMovingToPopover } from './utils/isFocusMovingToPopover';
import useExternalValueReset from './hooks/useExternalValueReset';
import { useDatasetData } from '../../../../../api/hooks/useDatasetData';
import useAnimatedDots from '../../../../../hooks/useAnimatedDots';
import usePopoverData from './hooks/usePopoverData';
import InputErrorMessage from '../../../InputErrorMessage/InputErrorMessage';
import Input from '../../../../Input/Input';
import { stringifyBracketParameter } from '../../../../../utils/apiRequests/stringifyBracketParameter';
import {
  DatasetDataApiRequestData,
  SubsetDataApiRequestData,
} from '../../../../../api/types/types';
import { getDatasetDataApiRequestData } from '../../../../../api/utils/requestData/getDatasetDataApiRequestData';
import { getSubsetDataApiRequestData } from '../../../../../api/utils/requestData/getSubsetDataApiRequestData';
import { useUserState } from '../../../../../hooks/context/useUserState';
import { apiRequest } from '../../../../../api/utils/apiRequest';
import { resolveTemplate } from '../../../../../utils/json/resolveTemplate';
import useApiErrorHandling from '../../../../../hooks/useApiErrorHandling';
import { getReferenceMasterScope } from './utils/getReferenceMasterScope';
import { useReferenceChipsSelectAllShortcut } from '../../../../../hooks/shortcuts/useReferenceChipsSelectAllShortcut';
import { useResponseActionsContext } from '../../../../../hooks/context/useResponseActionsContext';
import { getDataFromDatasetRequest } from '../../../../../api/utils/getDataFromDatasetRequest';
import { getReferenceValueDelimiter } from '../../../../../utils/getReferenceValueDelimiter';

const SCROLL_CONTAINER_SELECTOR = '.p-chips-multiple-container';
const CHIP_SELECTOR = '.p-chips-token';
const REFERENCE_INPUT_DECORATOR_CLASSNAME = 'reference-input-decorator';
const CHIP_REMOVE_SELECTOR = '.p-chips-token-icon';
const FULLSCREEN_TRIGGER_SELECTOR = '.reference-popover-toggle';
const REFERENCE_POPOVER_SELECTOR = '.reference-popover';
const POPOVER_FOOTER_SELECTOR = '.select-filters-footer';

type ReferenceInputProps = Omit<
  GridInputBaseProps,
  'name' | 'isColumnFilter' | 'value'
> & {
  value: ReferenceValue;
  control: ReferenceControl;
  referenceDatasets: any;
  setReferenceDatasets: Dispatch<any>;
  // resolvedFilter: pre-resolved filter from click handler; null = resolved to nothing, undefined = not resolved
  openFullscreenReference?: (
    resolvedFilter?: Record<string, any> | null
  ) => void;
  isFormInput?: boolean;
  form?: Form;
  formData?: any;
  // JSONata context for `control.filter`. Form scope passes its own request
  // data, filter scopes pass the surrounding dataset/subset one; both come from
  // RequestDataContext, read by the parent so this stays the single input here.
  requestData?: RequestDataValue;
};

const ReferenceInput = ({
  onChange,
  onTouch,
  onFocusIn,
  onFocusOut,
  value,
  error,
  setError,
  control,
  referenceDatasets,
  setReferenceDatasets,
  openFullscreenReference,
  isFormInput = false,
  disabled = false,
  form,
  formData,
  requestData,
}: ReferenceInputProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [chipsValue, setChipsValue] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedItems, setSelectedItems] = useState<ReferenceItem[]>([]);
  const [data, setData] = useState<any>(null);
  const [cachedQueryId, setCachedQueryId] = useState('');
  const [lazyLoading, setLazyLoading] = useState(false);
  const [disabledLazyLoading, setDisabledLazyLoading] = useState(false);
  const [fetchError, setFetchError] = useState<Error | null>(null);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Armed when a popover fetch is requested, not when it dispatches — the awaits in fetchReferenceData can hit the network, and until then `data` still holds the previous query's rows.
  const [isPopoverFetchPending, setIsPopoverFetchPending] =
    useState<boolean>(false);
  // True while the click handler awaits filter resolution before opening the
  // fullscreen modal. Disables the trigger to prevent double-clicks during
  // slow $apiLookup resolution.
  const [isOpeningFullscreen, setIsOpeningFullscreen] =
    useState<boolean>(false);
  const [
    shouldIgnoreDisplayedSingleSelectValue,
    setShouldIgnoreDisplayedSingleSelectValue,
  ] = useState<boolean>(false);

  const listRef = useRef<HTMLDivElement>(null);
  // Native input ref for single-select label replacement on click.
  const singleSelectInputRef = useRef<HTMLInputElement>(null);
  // Keep a direct Chips ref so multiReference focus stays on the real token input.
  const chipsRef = useRef<Chips | null>(null);
  // Unique Chips inputId — Prime derives its token ids from it, which aria-activedescendant points at.
  const chipsInputId = useId();
  const disabledLazyLoadinRef = useRef(disabledLazyLoading);
  const offsetRef = useRef(0);
  const lazyLoadingRef = useRef(lazyLoading);
  const cachedQueryIdRef = useRef(cachedQueryId);
  const inputValueRef = useRef(inputValue);
  // Skip the next focus-open cycle after chip removal to keep the remove click passive.
  const shouldSkipNextMultiSelectFocusOpenRef = useRef(false);
  // Tracks whether the last fresh fetch was filtered (label or value); used to detect stale filtered data.
  const lastFetchHadFilterRef = useRef(false);
  // Monotonic id of the latest fresh (non-lazy) fetch; used to discard out-of-order responses.
  const lastFreshRequestIdRef = useRef(0);
  // One-shot signal: user clicked Select All while not all data is loaded; finalize selection on next response.
  const pendingSelectAllRef = useRef(false);
  const { t } = useTranslate();

  const isMultiselect = control.multiSelect;
  const isPopoverVisible = isPopoverOpen && !disabled;
  const valueDelimiter = getReferenceValueDelimiter(isFormInput, isMultiselect);
  const labelItem = control.labelItem || control.idItem;
  // Format the label only when the referenced element is a dateTime column;
  // every other type passes through unchanged.
  const formatLabel = useMemo(
    () =>
      getReferenceLabelFormatter(
        referenceDatasets[control.datasetId],
        labelItem
      ),
    [referenceDatasets, control.datasetId, labelItem]
  );
  // A dateTime reference can't support quicksearch: the server stores raw values
  // (2026-06-02T12:26:45) but we display formatted ones (02. 06. 2026 12:26), and
  // a `contains` filter on the formatted text finds nothing. So for dateTime we
  // disable the inline autocomplete/list entirely and only format the selected or
  // manually typed value (fullscreen reference selection stays available).
  const isDateTimeReference = useMemo(
    () =>
      isDateTimeReferenceColumn(
        referenceDatasets[control.datasetId],
        labelItem
      ),
    [referenceDatasets, control.datasetId, labelItem]
  );
  const resolvedDecorator =
    control.decorator === null || typeof control.decorator === 'undefined'
      ? undefined
      : String(control.decorator);

  const { containerRef, hasHorizontalScroll } = useChipsScrollbarDetection(
    value,
    SCROLL_CONTAINER_SELECTOR,
    CHIP_SELECTOR
  );

  // Same handler pool that FormHandlersContext exposes — `$apiLookup` looks
  // funcIds up here, so reserved keys (default/get/insert/update) are excluded.
  const handlerApiMethods = useMemo<Record<string, ApiMethod>>(
    () => getHandlerApiMethods(form?.apiMethods),
    [form?.apiMethods]
  );
  // Forwarded into createApiLookupFunction so $apiLookup API failures
  // surface via the standard toast/session handler.
  const handleApiError = useApiErrorHandling();

  const {
    data: fetchedDataset,
    fetch: fetchDataset,
    error: datasetError,
    isLoading: datasetLoading,
  } = useDataset(control.datasetId);

  const {
    data: datasetData,
    fetch: fetchData,
    detailedFetch: detailedFetchData,
    error: dataError,
  } = useDatasetData();

  // Isolated channel for background label lookup — its response feeds only into selectedItems. Kept separate from the popover fetch so concurrent responses cannot batch and overwrite each other in a single useDatasetData state.
  const {
    data: valueLookupData,
    fetch: valueLookupFetch,
    detailedFetch: valueLookupDetailedFetch,
    isLoading: isValueLookupLoading,
  } = useDatasetData();

  const {
    state: { token },
  } = useUserState();
  const { getResponseActionHandler } = useResponseActionsContext();

  // Ctrl/Cmd+A → mark all chips selected (CSS class on container); next Backspace/Delete clears them. Multi-only; for single it's a no-op (no chips). Includes the popover/list scope so the shortcut still fires while the user is selecting items inside the dropdown (portal-rendered, outside containerRef).
  const chipsSelectAllScopes = useMemo(() => [containerRef, listRef], []);
  const { selectedAll: chipsSelectAll, clearSelectedAll: clearChipsSelectAll } =
    useReferenceChipsSelectAllShortcut({
      scopeRefs: chipsSelectAllScopes,
      disabled,
      hasChips: Boolean(isMultiselect) && selectedItems.length > 0,
      onClearAll: () => {
        // Ctrl+A marked the chips and natively selected the typed text, so the confirm key clears both.
        setNewSelectedItems([], true);
        submitItems([]);
      },
    });

  const removeChipAt = (index: number) => {
    const newSelectedItems = selectedItems.filter((_, i) => i !== index);
    setNewSelectedItems(newSelectedItems);
    submitItems(newSelectedItems);
  };

  // Arrow keys walk the chips once the caret reaches the start of the input; Backspace/Delete removes the highlighted chip.
  const { focusedChipIndex, onChipsNavKeyDownCapture, clearChipFocus } =
    useChipsArrowNavigation({
      containerRef,
      // selectedItems is the walk's source of truth (removeChipAt filters it); chipsValue only mirrors its labels.
      chipsCount: isMultiselect ? selectedItems.length : 0,
      disabled,
      onRemoveChipAt: removeChipAt,
      // The walk claims keys before the window-level Ctrl+A listener — its visual select-all must drop with it.
      onTakeover: clearChipsSelectAll,
    });

  const throttledRequest = useThrottle(() => {
    const searchValue = getCurrentSingleSelectSearchValue();
    const labelFilter = searchValue.length >= 1 ? searchValue : null;
    fetchReferenceData(
      referenceDatasets[control.datasetId],
      false,
      labelFilter
    );
  }, 750);

  // Last filter that was sent with a fetch (after JSONata resolution).
  // Used by the post-response useEffect to subtract the default filter from
  // the api string when deciding `allDataLoaded`. State so React rerenders
  // when the dynamic filter changes between fetches.
  const [resolvedFilter, setResolvedFilter] = useState<Record<
    string,
    any
  > | null>(control.filter && !isFormInput ? control.filter : null);

  const defaultFilterQuery: string | null = useMemo(() => {
    if (resolvedFilter) {
      return stringifyBracketParameter({ filter: resolvedFilter });
    }
    return null;
  }, [resolvedFilter]);

  useEffect(() => {
    lazyLoadingRef.current = lazyLoading;
  }, [lazyLoading]);

  useEffect(() => {
    cachedQueryIdRef.current = cachedQueryId;
  }, [cachedQueryId]);

  useEffect(() => {
    disabledLazyLoadinRef.current = disabledLazyLoading;
  }, [disabledLazyLoading]);

  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  useEffect(() => {
    // Ignore only the unchanged selected label in single-select mode.
    if (isMultiselect) {
      setShouldIgnoreDisplayedSingleSelectValue(false);
      return;
    }

    const selectedLabel = selectedItems[0]?.label ?? '';
    setShouldIgnoreDisplayedSingleSelectValue(
      selectedItems.length === 1 && inputValue === selectedLabel
    );
  }, [inputValue, isMultiselect, selectedItems]);

  useDebouncedEffect(
    () => {
      if (
        !isFormInput ||
        (!isEmptyValue(value) &&
          control.labelItem &&
          control.idItem !== control.labelItem)
      ) {
        if (!referenceDatasets[control.datasetId]) {
          fetchDataset();
        } else if (
          referenceDatasets[control.datasetId] &&
          !isEmptyValue(value)
        ) {
          const splittedValue = isMultiselect
            ? (value as string).split(valueDelimiter)
            : [value];
          // Prevents refetching data after selecting items in popover
          const includesNotFetchedItems = splittedValue.some(
            (id: ReferenceValue) =>
              !selectedItems.some(({ value }) => value === id)
          );
          if (includesNotFetchedItems) {
            fetchReferenceData(
              referenceDatasets[control.datasetId],
              false,
              null,
              value
            );
          }
        }
      }
      if (
        isFormInput &&
        !isEmptyValue(value) &&
        (!control.labelItem || control.labelItem === control.idItem)
      ) {
        // The label is the idItem value itself; the dataset definition is the
        // only way to know whether that column is a dateTime needing formatting.
        // Until it loads we can't format, so fetch it and defer building the
        // label — the input shows a loading state meanwhile
        // (isResolvingLabel). When the definition lands, formatLabel
        // becomes dateTime-aware and this effect re-runs (formatLabel is in deps).
        if (!referenceDatasets[control.datasetId]) {
          if (!datasetLoading) {
            fetchDataset();
          }
          return;
        }
        const splittedValue = isMultiselect
          ? (value as string).split(valueDelimiter)
          : [value];
        const newSelectedItems: ReferenceItem[] = [];
        splittedValue.forEach((v: ReferenceValue) => {
          const value = String(v);
          newSelectedItems.push({
            value,
            label: formatLabel(value),
          });
        });
        setNewSelectedItems(newSelectedItems);
      }
    },
    [control.datasetId, value, formatLabel],
    100
  );

  useExternalValueReset({
    isFormInput,
    value,
    selectedItems,
    isMultiselect,
    delimiter: valueDelimiter,
    setSelectedItems,
    setChipsValue,
    setInputValue,
  });

  useEffect(() => {
    if (
      fetchedDataset?.apiMethod &&
      fetchedDataset?.datasetId === control.datasetId
    ) {
      // Skip value-only preload when single-select restores its own label.
      const shouldFetchSelectedValueAfterDatasetLoad =
        !isEmptyValue(value) &&
        (!isFormInput ||
          (control.labelItem && control.idItem !== control.labelItem));

      setReferenceDatasets((v) => ({
        ...v,
        [control.datasetId]: fetchedDataset,
      }));
      if (shouldFetchSelectedValueAfterDatasetLoad) {
        fetchReferenceData(fetchedDataset, false, null, value);
      }
      // Not an else: the value lookup runs on its own channel and never fills the list.
      if (isPopoverOpen) {
        // Reuse effective search term after form restore.
        fetchReferenceData(
          fetchedDataset,
          false,
          getCurrentSingleSelectSearchValue()
        );
      }
    }
  }, [fetchedDataset]);

  useEffect(() => {
    if (datasetData && datasetData.datasetId === control.datasetId) {
      // Discard a fresh response superseded by a newer fresh fetch (e.g. the on-open
      // unfiltered fetch resolving after a filtered one). Lazy pages stay untagged.
      if (
        !datasetData.lazy &&
        datasetData.requestId !== undefined &&
        datasetData.requestId !== lastFreshRequestIdRef.current
      ) {
        return;
      }

      const newData = datasetData.data;
      if (datasetData.data.length < GRID_DATA_LIMIT) {
        setDisabledLazyLoading(true);

        // Only mark allDataLoaded on unfiltered fetch; URL-based detection is unreliable
        // for Object-API datasets, so use lastFetchHadFilterRef instead.
        if (!lastFetchHadFilterRef.current) {
          setAllDataLoaded(true);
        }
      }
      if (datasetData.lazy) {
        if (
          datasetData.cachedQueryId === cachedQueryId ||
          // Use lazyloading withoud server cache id if server did not return X-CACHEDQUERY-ID
          datasetData.cachedQueryId === undefined
        ) {
          setData((oldData) => {
            if (!oldData) {
              return newData;
            }
            return [...oldData, ...newData];
          });
          // Sync selectedItems with newly loaded rows: add any IDs from value
          // not yet in selectedItems (lazy page may carry them). Display-only.
          if (value) {
            const additionalItems = getAdditionalSelectedItems({
              value,
              newData,
              idItem: control.idItem,
              labelItem,
              selectedItems,
              delimiter: valueDelimiter,
              formatLabel,
            });
            if (additionalItems.length > 0) {
              setNewSelectedItems([...selectedItems, ...additionalItems]);
            }
          }
          // Advance offset so next loadMoreData fetches the correct next page
          offsetRef.current += newData.length;
        } else {
          // Reset data if X-CACHEDQUERY-ID changed
          toast(t('toasts.newDataLoaded'));
          offsetRef.current = 0;
          // Scroll to the top of lsit
          scrollListToTop();
          setData(newData);
        }
      } else {
        setData(newData);
        // Advance offset after fresh fetch so first lazy page starts after the loaded items
        offsetRef.current = newData.length;
        scrollListToTop();
      }
      if (datasetData.cachedQueryId !== undefined) {
        setCachedQueryId(datasetData.cachedQueryId);
      }
      setIsLoading(false);
      setIsPopoverFetchPending(false);
      setLazyLoading(false);
      // Clear any previous error only after successful data load
      setFetchError(null);
      // Finalize a pending Select All on this fresh (non-lazy) response.
      if (pendingSelectAllRef.current && !datasetData.lazy) {
        pendingSelectAllRef.current = false;
        // Block lazy load: fetchAll already returned every matching row, so a scroll
        // to the bottom would otherwise fire a wasted page request.
        setDisabledLazyLoading(true);
        // Mark allDataLoaded only when the fetch was unfiltered. For filtered fetchAll,
        // leave it false so clearing the input later refetches the unfiltered dataset.
        if (!lastFetchHadFilterRef.current) {
          setAllDataLoaded(true);
        }
        const items: ReferenceItem[] = newData.map((item) => ({
          value: item[control.idItem].toString(),
          label: formatLabel(
            control.labelItem
              ? String(item[control.labelItem])
              : item[control.idItem].toString()
          ),
        }));
        // Select All adds the fetched rows to the current selection, it never replaces it.
        const merged = mergeSelectedItems(selectedItems, items);
        // Don't reset inputValue so the active filter stays visible after selecting all.
        setError(null);
        setNewSelectedItems(merged, false);
        submitItems(merged);
        focusMultiSelectInputToEnd();
      }
    }
  }, [datasetData]);

  useEffect(() => {
    if (value && data && selectedItems.length === 0) {
      const newSelectedItems = getSelectedItemsFromValue({
        value,
        data,
        idItem: control.idItem,
        labelItem,
        isMultiselect,
        delimiter: valueDelimiter,
        formatLabel,
      });
      setNewSelectedItems(newSelectedItems);
    }
  }, [data]);

  // Background label-lookup response — populates chip labels for pre-selected values that may live on a page the popover has not loaded yet. Merges into selectedItems (adds any missing entries) without touching `data`, so the popover's paged stream stays authoritative.
  useEffect(() => {
    if (!valueLookupData || valueLookupData.datasetId !== control.datasetId) {
      return;
    }
    const items: ReferenceItem[] = valueLookupData.data.map((row) => ({
      value: row[control.idItem].toString(),
      // Same label shape as every other producer — raw values would break label-based matching.
      label: control.labelItem
        ? formatLabel(String(row[control.labelItem]))
        : row[control.idItem].toString(),
    }));
    const merged = [...selectedItems];
    items.forEach((item) => {
      if (!merged.some(({ value: v }) => v === item.value)) {
        merged.push(item);
      }
    });
    if (merged.length !== selectedItems.length) {
      setNewSelectedItems(merged);
    }
  }, [valueLookupData]);

  useEffect(() => {
    if (datasetError && datasetError.datasetId === control.datasetId) {
      setFetchError(datasetError.error);
      // Stop loading spinner on error — data will never arrive
      setIsLoading(false);
      setIsPopoverFetchPending(false);
      setLazyLoading(false);
      // Discard pending Select All so it does not trigger on a later unrelated response.
      pendingSelectAllRef.current = false;
    } else if (dataError && dataError.datasetId === control.datasetId) {
      setFetchError(dataError.error);
      // Stop loading spinner on error — data will never arrive
      setIsLoading(false);
      setIsPopoverFetchPending(false);
      setLazyLoading(false);
      // Discard pending Select All so it does not trigger on a later unrelated response.
      pendingSelectAllRef.current = false;
    }
  }, [datasetError, dataError]);

  // Clear validation error when fullscreen submits with ≥1 row — value becomes non-empty
  // via external state update (DatasetDetailReference or DatasetReference), bypassing onItemSelect.
  useEffect(() => {
    if (!isEmptyValue(value)) {
      setError(null);
    }
  }, [value]);

  useEffect(() => {
    // Skip when inputValue just settled onto the canonical selected label (background label lookup or restore) — that is not a user search intent, so we would only be re-fetching the popover with the same effective empty filter.
    const isRestoredSelectedLabel =
      !isMultiselect &&
      selectedItems.length === 1 &&
      selectedItems[0].label === inputValue;
    if (
      !isDateTimeReference &&
      !allDataLoaded &&
      referenceDatasets[control.datasetId] &&
      isPopoverOpen &&
      !isRestoredSelectedLabel &&
      (inputValue.length === 0 || inputValue.length >= 1 || data?.length === 0)
    ) {
      setLazyLoading(true);
      setCachedQueryId('');
      cachedQueryIdRef.current = '';
      throttledRequest();
    } else if (allDataLoaded) {
      scrollListToTop();
    }
  }, [inputValue]);

  // Closing the popover (Apply, Enter/Esc, click outside) discards the unconfirmed quick-filter text: the chips alone carry the value, and a kept filter would silently pre-filter the next open (JS-1256 review).
  useEffect(() => {
    if (isPopoverOpen || !isMultiselect) return;
    if (inputValue !== '') setInputValue('');
  }, [isPopoverOpen]);

  // Keep the chip-input visible at the end of the horizontal chip strip. Fires on chipsValue (label lookup arrival, add/remove) and inputValue (typing) so an injected quick-filter char, a resolved chip label, or a fresh open all converge on a visible input+caret at the right edge. Runs even when inputValue is empty because the empty input still sits after every chip.
  useEffect(() => {
    if (!isMultiselect) return;
    // During the arrow-key chip walk the strip follows the highlighted chip, not the input.
    if (focusedChipIndex !== null) return;
    const scrollContainer = containerRef.current?.querySelector(
      SCROLL_CONTAINER_SELECTOR
    );
    if (!(scrollContainer instanceof HTMLElement)) return;
    requestAnimationFrame(() => {
      scrollContainer.scrollLeft = scrollContainer.scrollWidth;
    });
  }, [isMultiselect, chipsValue, inputValue, focusedChipIndex]);

  const scrollListToTop = () => {
    if (listRef.current && listRef.current.scrollTop !== 0) {
      listRef.current.scrollTo({ top: 0 });
    }
  };

  const submitItems = (newSelectedItems: ReferenceItem[]) => {
    const newValue = newSelectedItems
      .map(({ value }) => value)
      .join(valueDelimiter);
    onChange(newValue);
    onTouch?.(newValue);
  };

  const setNewSelectedItems = (
    newSelectedItems: ReferenceItem[],
    resetInput = false
  ) => {
    setSelectedItems(newSelectedItems);
    if (isMultiselect) {
      setChipsValue(newSelectedItems.map(({ label }) => label));
      if (resetInput) {
        setInputValue('');
      }
    } else if (newSelectedItems[0]) {
      setInputValue(newSelectedItems[0].label);
    }
  };

  const fetchReferenceData = async (
    dataset: Dataset | undefined,
    lazy = false,
    labelFilter: string | null = null,
    valueFilter: ReferenceValue | null = null,
    fetchAll = false
  ) => {
    if (!dataset) {
      // No request will be made, so a queued Select All must not be served by an unrelated later response.
      pendingSelectAllRef.current = false;
      return;
    }

    // dateTime reference: never send a label `contains` filter. The server stores
    // raw datetimes (2026-06-02T12:26:45) but the user types against the formatted
    // display (02. 06. 2026), so the server would match nothing. Always fetch the
    // unfiltered list; typed narrowing is client-side only (usePopoverData). The
    // value `in` filter (preselected ids) still applies.
    if (isDateTimeReference) {
      labelFilter = null;
    }

    // The backend `in` filter is always comma-separated; normalize the form's
    // `;`-delimited value back to commas before it goes on the wire.
    const inFilterValue =
      valueFilter != null
        ? String(valueFilter).split(valueDelimiter).join(',')
        : valueFilter;

    // Captured synchronously so a concurrent fetch bumping the ref can't reassign this request's id.
    let requestId: number | undefined;

    const isValueLookup = Boolean(valueFilter) && !fetchAll;

    if (!isValueLookup) {
      setIsPopoverFetchPending(true);
    }

    // Fresh fetch resets pagination; lazy load continues from current offset via ref. Value lookup runs on its own useDatasetData channel, so it never touches the popover's pagination/id state.
    if (!lazy && !isValueLookup) {
      offsetRef.current = 0;
      // Track whether this fetch is filtered — used to guard allDataLoaded logic.
      lastFetchHadFilterRef.current = Boolean(labelFilter);
      lastFreshRequestIdRef.current += 1;
      requestId = lastFreshRequestIdRef.current;
      // Reset lazy-loading guard so a previous filtered fetch (e.g. value-resolution)
      // returning < GRID_DATA_LIMIT rows does not permanently block loadMoreData.
      setDisabledLazyLoading(false);
    }

    const filters: Record<string, any> = {};

    // Label lookup drops control.filter so a unique idItem alone resolves the row.
    const isLabelLookup = isReferenceLabelLookup({
      valueFilter,
      idItem: control.idItem,
      labelItem: control.labelItem,
    });

    // Resolve dynamic `control.filter` via JSONata; string filters need resolution before use.
    // Logic extracted into resolveControlFilter so the same code is reused at
    // click-time (pre-resolution before opening FullscreenReference).
    let effectiveFilter: Record<string, any> | undefined =
      typeof control.filter === 'string' ? undefined : control.filter;
    let hasFreshFilter = false;
    if (control.filter && requestData) {
      // .catch: createRequestBody runs outside resolveControlFilter's own try, so a throw there must abort like a failed resolution instead of rejecting silently.
      const result = await resolveControlFilter({
        rawFilter: control.filter,
        formDefinition: form,
        formValues: (formData as Record<string, unknown>) ?? {},
        requestData,
        handlerApiMethods,
        handleApiError,
      }).catch(() => null);
      if (!result || !result.ok) {
        // JSONata error or $apiLookup failure — abort fetch and surface error.
        // (handleApiError has already toasted/logged the cause for $apiLookup.)
        setFetchError(
          result?.error ?? new Error('reference filter expression failed')
        );
        setIsLoading(false);
        setIsPopoverFetchPending(false);
        setLazyLoading(false);
        return;
      }
      hasFreshFilter = true;
      effectiveFilter = result.filter;
      // Push into state so defaultFilterQuery (used by the post-response
      // allDataLoaded check) reflects what actually went on the wire.
      setResolvedFilter(effectiveFilter ?? null);
    }

    // Filter forms seed `resolvedFilter` with the raw template and the state
    // lags this call by a render, so the string-api query must use what was
    // resolved right here. Form inputs keep the state-derived query.
    let filterQuery = defaultFilterQuery;
    if (!isFormInput && hasFreshFilter) {
      filterQuery = effectiveFilter
        ? stringifyBracketParameter({ filter: effectiveFilter })
        : null;
    }

    if (effectiveFilter && !isLabelLookup)
      Object.assign(filters, effectiveFilter);

    if (labelFilter) {
      filters[labelItem] = {
        ...(filters[labelItem] || {}),
        contains: labelFilter,
      };
    }

    if (valueFilter) {
      filters[control.idItem] = {
        ...(filters[control.idItem] || {}),
        in: inFilterValue,
      };
    }

    const multiSort = getMultisortFromDataset(dataset);
    // Drop limit for unbounded fetches: value-resolution (preselected IDs) and Select All
    // (user wants every matching row regardless of pagination).
    const limit = valueFilter || fetchAll ? undefined : GRID_DATA_LIMIT;
    // When the form runs under a master row (slave/subset reference), requestData
    // carries master.id — fetch the subset (rows scoped to that master) instead of
    // the full dataset. Mirrors DatasetContext's subset/dataset branch.
    // Reset BEFORE building request data — a fresh fetch must omit the previous
    // cache id so the server reads current rows.
    if (!lazy && !isValueLookup) {
      setCachedQueryId('');
      cachedQueryIdRef.current = '';
    }

    const master = getReferenceMasterScope(requestData, isFormInput);
    const apiRequestData: DatasetDataApiRequestData | SubsetDataApiRequestData =
      master
        ? getSubsetDataApiRequestData(
            master.id,
            dataset,
            multiSort || [],
            token,
            offsetRef.current,
            filters,
            GRID_DATA_LIMIT,
            cachedQueryIdRef.current,
            undefined,
            master.idItem
          )
        : getDatasetDataApiRequestData(
            dataset,
            multiSort || [],
            token,
            offsetRef.current,
            filters,
            GRID_DATA_LIMIT,
            cachedQueryIdRef.current
          );
    if (!limit) {
      // limit is optional in DatasetDataApiRequestData — omit it for value-resolution fetches
      delete apiRequestData.limit;
    }
    // Restrict to id + label columns only; dedup when labelItem falls back to idItem.
    apiRequestData.items = Array.from(new Set([control.idItem, labelItem]));

    // String api method request flow
    if (typeof dataset.apiMethod === 'string') {
      // A malformed {{ }} in apiMethod rejects before the spinner is armed, so surface it as fetchError instead of leaving the popover loading forever.
      const resolvedApi = await resolveTemplate(
        { apiUrl: dataset.apiMethod },
        apiRequestData
      ).catch(() => null);
      if (!resolvedApi) {
        setFetchError(new Error('reference api method expression failed'));
        setIsLoading(false);
        setIsPopoverFetchPending(false);
        setLazyLoading(false);
        return;
      }
      const { apiUrl } = resolvedApi;

      const paramsString = getReferenceParamsString(
        control,
        offsetRef.current,
        labelFilter,
        inFilterValue,
        isLabelLookup ? null : filterQuery,
        labelItem,
        limit
      );

      // Background label lookup runs on its own useDatasetData channel; it must not touch the popover's spinner and its response goes through the dedicated valueLookupData effect below.
      if (isValueLookup) {
        valueLookupFetch(
          `${apiUrl}${paramsString}`,
          dataset,
          lazy,
          cachedQueryIdRef.current,
          undefined,
          undefined,
          requestId
        );
        return;
      }
      setIsLoading(true);
      fetchData(
        `${apiUrl}${paramsString}`,
        dataset,
        lazy,
        cachedQueryIdRef.current,
        undefined,
        undefined,
        requestId
      );
      return;
    }

    // Object api method request flow — set loading before async dispatch (mirrors string API path)
    if (isValueLookup) {
      apiRequest(
        dataset.apiMethod,
        apiRequestData,
        (apiMethod) =>
          getDataFromDatasetRequest(() =>
            valueLookupDetailedFetch(
              apiMethod,
              dataset,
              lazy,
              undefined,
              undefined,
              requestId
            )
          ),
        getResponseActionHandler
      );
      return;
    }
    setIsLoading(true);
    apiRequest(
      dataset.apiMethod,
      apiRequestData,
      (apiMethod) =>
        getDataFromDatasetRequest(() =>
          detailedFetchData(
            apiMethod,
            dataset,
            lazy,
            undefined,
            undefined,
            requestId
          )
        ),
      getResponseActionHandler
    );
  };

  // Click-time pre-resolution before opening FullscreenReference. The modal's
  // route unmounts the form context, so it cannot resolve $apiLookup or
  // {{ form.* }} placeholders itself. Resolve here while form context is live,
  // then forward the resolved object filter to the modal via openFullscreenReference.
  const handleOpenFullscreenReferenceClick = async (): Promise<void> => {
    if (!openFullscreenReference) return;
    // No resolving scope at all — pass raw filter through.
    if (!control.filter || !requestData) {
      openFullscreenReference();
      return;
    }
    setIsOpeningFullscreen(true);
    const result = await resolveControlFilter({
      rawFilter: control.filter,
      formDefinition: form,
      formValues: (formData as Record<string, unknown>) ?? {},
      requestData,
      handlerApiMethods,
      handleApiError,
    });
    setIsOpeningFullscreen(false);
    if (!result.ok) {
      // Surface the error via the inline popover ApiError (same UI as
      // inline-fetch failures); do not open the modal.
      setFetchError(result.error);
      setIsPopoverOpen(true);
      return;
    }
    // Distinguish "resolved to nothing" (null) from "didn't resolve"
    // (no arg). Without this distinction Form.tsx can't tell whether to strip
    // the raw filter or keep it, and would leak unresolved JSONata to the modal.
    openFullscreenReference(result.filter ?? null);
  };

  const addTypedChip = (item: ReferenceItem, resetInput: boolean): void => {
    const newSelectedItems = [...selectedItems, item];
    setNewSelectedItems(newSelectedItems, resetInput);
    submitItems(newSelectedItems);
  };

  const onManualInputMultiSelect = (e) => {
    // Auto-focus suppresses the open-on-focus popover; the first keystroke is a real
    // search intent, so open the quicksearch if it isn't already.
    if (!isPopoverOpen) {
      openPopover();
    }
    const typedText = e.target.value;
    setInputValue(typedText);
    if (!data) return;
    const { exactMatch, hasLongerOption } = getTypeToSelectMatch({
      typedText,
      data,
      idItem: control.idItem,
      labelItem,
      selectedLabels: chipsValue,
      formatLabel,
    });
    // Auto-select only an unambiguous complete name; a longer option means the user may still be typing.
    if (exactMatch && !hasLongerOption) {
      addTypedChip(exactMatch, true);
    }
  };

  const onManualInputSingleSelect = (value) => {
    // Auto-focus suppresses the open-on-focus popover; the first keystroke is a real
    // search intent, so open the quicksearch if it isn't already.
    if (!isPopoverOpen) {
      openPopover();
    }
    setInputValue(value);
    setError(null);
    // User typing re-enables search by current input.
    setShouldIgnoreDisplayedSingleSelectValue(false);
    if (!data) return;
    const { exactMatch, hasLongerOption } = getTypeToSelectMatch({
      typedText: value,
      data,
      idItem: control.idItem,
      labelItem,
      selectedLabels: [],
      formatLabel,
    });
    // Commit only an unambiguous complete name; Enter or blur confirms one that is a prefix of others.
    if (exactMatch && !hasLongerOption) {
      // setNewSelectedItems resets inputValue to the canonical label on match,
      // which triggers shouldIgnoreDisplayedSingleSelectValue and stops the filter.
      setNewSelectedItems([exactMatch]);
      submitItems([exactMatch]);
      return;
    }
    // The typed text no longer names the committed value — clear it once.
    if (selectedItems.length > 0) {
      setNewSelectedItems([]);
      submitItems([]);
    }
  };

  const onSingleSelectInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    // ArrowDown/click into the popover blurs the input; that's navigation, not an invalid entry.
    const skipValidation = isFocusMovingToPopover(
      event.relatedTarget,
      REFERENCE_POPOVER_SELECTOR
    );
    if (!skipValidation && inputValue !== '') {
      const isSelected = selectedItems.some(
        ({ label }) => label.toLowerCase() === inputValue.toLowerCase()
      );
      if (!isSelected && data) {
        const { exactMatch } = getTypeToSelectMatch({
          typedText: inputValue,
          data,
          idItem: control.idItem,
          labelItem,
          selectedLabels: [],
          formatLabel,
        });
        // Leaving the field confirms an exactly typed name that stayed uncommitted as a prefix of longer options.
        if (exactMatch) {
          setNewSelectedItems([exactMatch]);
          submitItems([exactMatch]);
          onFocusOut?.();
          return;
        }
      }
      setError(isSelected ? null : [t('errors.invalidOption')]);
    }
    onFocusOut?.();
  };

  const onRemoveChip = (e: ChipsRemoveEvent) => {
    // Prime Chips refocuses the input after remove; skip that one focus reopen.
    shouldSkipNextMultiSelectFocusOpenRef.current = true;
    // By index, not by the event's label — two chips may share a label while holding different ids.
    const removedIndex = getRemovedChipIndex(
      e,
      containerRef,
      selectedItems.length
    );
    // Desynced or empty selection — nothing on our side to remove or submit.
    if (removedIndex < 0) return;
    const wasLastChip = removedIndex === selectedItems.length - 1;
    const newSelectedItems = selectedItems.filter((_, i) => i !== removedIndex);
    setSelectedItems(newSelectedItems);
    submitItems(newSelectedItems);
    // Deleting the rightmost chip ends the arrow-key walk — put the caret back into the input.
    if (wasLastChip) focusMultiSelectInputToEnd();
  };

  const onMultiSelectFocus = () => {
    if (shouldSkipNextMultiSelectFocusOpenRef.current) {
      shouldSkipNextMultiSelectFocusOpenRef.current = false;
      return;
    }

    // Auto-focus on form open focuses the field but must not pop the quicksearch.
    if (!isOpenOnFocusSuppressed()) {
      openPopover();
    }
    onFocusIn?.();
  };

  const onMultiSelectBlur = () => {
    // Chips refocuses itself after chip remove; don't emit focus-out for that.
    if (shouldSkipNextMultiSelectFocusOpenRef.current) return;
    onFocusOut?.();
  };

  const loadMoreData = () => {
    if (!lazyLoadingRef.current && !disabledLazyLoadinRef.current) {
      setLazyLoading(true);
      lazyLoadingRef.current = true;
      fetchReferenceData(
        referenceDatasets[control.datasetId],
        true,
        getCurrentSingleSelectSearchValue()
      );
    }
  };

  const selectAllItems = (preserveFocus = false) => {
    // Fast path: select from memory. Also skips fetch when filtered results fit one page
    // (data.length < GRID_DATA_LIMIT means the backend has no more matching rows).
    if (data && (allDataLoaded || data.length < GRID_DATA_LIMIT)) {
      const items: ReferenceItem[] = (popoverData ?? data).map((item) => ({
        value: item[control.idItem].toString(),
        label: formatLabel(
          control.labelItem
            ? String(item[control.labelItem])
            : item[control.idItem].toString()
        ),
      }));
      // Select All adds the listed rows to the current selection, it never replaces it.
      const merged = mergeSelectedItems(selectedItems, items);
      // Don't reset inputValue so the active filter stays visible after selecting all.
      setError(null);
      setNewSelectedItems(merged, false);
      submitItems(merged);
      if (!preserveFocus) focusMultiSelectInputToEnd();
      return;
    }
    // Slow path: fetch all rows matching the current label filter (pagination dropped),
    // then finalize the selection inside the dataset-data effect via pendingSelectAllRef.
    pendingSelectAllRef.current = true;
    fetchReferenceData(
      referenceDatasets[control.datasetId],
      false,
      currentSearchValue || null,
      null,
      true
    );
  };

  const onClickOutsidePopover = (e) => {
    const target = e.target;
    // Prevents closing popover when clicking on the delete chip button
    if (
      !(target instanceof HTMLElement) ||
      !target.closest(CHIP_REMOVE_SELECTOR)
    ) {
      setIsPopoverOpen(false);
    }
  };

  const onItemSelect = (newSelectedItems: ReferenceItem[]) => {
    setError(null);
    // Keep the quick filter text and its rows so the user can pick more filtered items or keep typing (JS-1256).
    setNewSelectedItems(newSelectedItems);
    submitItems(newSelectedItems);
  };

  const openPopover = () => {
    if (disabled) return;
    // Block while initial value labels are still being resolved to avoid showing stale data.
    if (isResolvingInitialValue) return;
    if (!referenceDatasets[control.datasetId] && !datasetLoading) {
      fetchDataset();
    }
    // ReferencePopover fires its own on-mount fetch with the current search term, so single-select restore doesn't need a duplicate fetch here.
    setIsPopoverOpen(true);
  };

  const focusMultiSelectInputToEnd = () =>
    focusMultiSelectInputToEndHandler({
      chipsRef,
      containerRef,
      scrollContainerSelector: SCROLL_CONTAINER_SELECTOR,
    });

  const onMultiSelectWrapperMouseDown = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    handleMultiSelectWrapperMouseDown(event, {
      isMultiselect: Boolean(isMultiselect),
      openPopover,
      focusMultiSelectInputToEnd,
      scrollContainerSelector: SCROLL_CONTAINER_SELECTOR,
      chipRemoveSelector: CHIP_REMOVE_SELECTOR,
      fullscreenTriggerSelector: FULLSCREEN_TRIGGER_SELECTOR,
    });
  };

  const getCurrentSingleSelectSearchValue = () =>
    getSingleSelectSearchValue({
      isMultiselect,
      shouldIgnoreDisplayedSingleSelectValue,
      inputValue,
    });

  const currentSearchValue = getCurrentSingleSelectSearchValue();
  const popoverData = usePopoverData({
    data,
    allDataLoaded,
    currentSearchValue,
    labelItem,
    formatLabel,
  });

  const isResolvingLabel = getIsResolvingLabel({
    isFormInput,
    value,
    control,
    dataset: referenceDatasets[control.datasetId],
  });

  // True only while fetching labels for pre-selected IDs — filter reopen gap before chips appear
  const isResolvingInitialValue =
    (!isEmptyValue(value) && selectedItems.length === 0 && isLoading) ||
    isResolvingLabel;

  // Display-only: openPopover keeps using isResolvingInitialValue so its timing is unchanged.
  const isResolvingDisplayedLabel = getIsResolvingDisplayedLabel({
    isResolvingInitialValue,
    value,
    selectedItems,
    isValueLookupLoading,
  });

  const animatedDots = useAnimatedDots(isResolvingDisplayedLabel);
  const animatedLoadingText = `${t('loading')}${animatedDots}`;

  const onSingleSelectInputClick = (event: MouseEvent<HTMLInputElement>) => {
    // Select full label so typing replaces it.
    event.currentTarget.select();
    openPopover();
  };

  // Focuses the first suggestion row in the popover. Deferred a frame so it runs after the popover finishes its open-time focus settling — otherwise our focus is overwritten and the visual highlight is lost until the next ArrowDown.
  const focusFirstPopoverItem = () => {
    window.requestAnimationFrame(() => {
      getPopoverItemRows(listRef)[0]?.focus();
    });
  };

  // Lets native Tab walk input → fullscreen button → popover; after each Tab, redirects focus into the popover only if it would otherwise leave the wrapper. ArrowDown opens the popover (when closed) or jumps focus into its first row (when open).
  const onWrapperKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isPopoverOpen) {
        openPopover();
        return;
      }
      focusFirstPopoverItem();
      return;
    }

    if (!isPopoverOpen) return;
    if (event.key !== 'Tab' || event.shiftKey) return;

    const wrapper = event.currentTarget;
    window.requestAnimationFrame(() => {
      if (!wrapper.contains(document.activeElement)) {
        focusFirstPopoverItem();
      }
    });
  };

  // Enter typed into the chip input confirms an exactly typed name even when longer options exist; the popover stays open. Runs before Prime's own keydown, which bails on defaultPrevented.
  const onMultiSelectInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key !== 'Enter' || !isPopoverOpen) return;
    if (!data || inputValue === '') return;
    const { exactMatch, hasLongerOption } = getTypeToSelectMatch({
      typedText: inputValue,
      data,
      idItem: control.idItem,
      labelItem,
      selectedLabels: chipsValue,
      formatLabel,
    });
    // No match — the container handler closes the popover as before.
    if (!exactMatch) return;
    event.preventDefault();
    event.stopPropagation();
    // A longer option still matches the typed text — keep it as the filter
    // so the user can continue typing towards it; otherwise clear it.
    addTypedChip(exactMatch, !hasLongerOption);
  };

  // Sits on the outer container (React parent of both the input wrapper and the popover content) so Enter/Escape are caught whether focus is on the input or on a popover row. Both close the popover and return focus to the input without auto-reopening, and stop bubbling so the column-filter Apply handler never sees it.
  const onContainerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isPopoverOpen) return;
    if (event.key !== 'Enter' && event.key !== 'Escape') return;
    // Footer buttons own their Enter — ion-button has no native Enter activation, so click it explicitly.
    if (
      event.key === 'Enter' &&
      event.target instanceof HTMLElement &&
      event.target.closest(POPOVER_FOOTER_SELECTOR)
    ) {
      event.preventDefault();
      event.stopPropagation();
      const footerButton = event.target.closest('ion-button');
      if (footerButton instanceof HTMLElement) footerButton.click();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    // Single select: Enter picks the exactly typed name and the close below applies it.
    if (event.key === 'Enter' && !isMultiselect && data && inputValue !== '') {
      const { exactMatch } = getTypeToSelectMatch({
        typedText: inputValue,
        data,
        idItem: control.idItem,
        labelItem,
        selectedLabels: [],
        formatLabel,
      });
      if (exactMatch) {
        setNewSelectedItems([exactMatch]);
        submitItems([exactMatch]);
      }
    }
    setIsPopoverOpen(false);
    // Multiselect's Chips onFocus would auto-reopen the popover; this flag is consumed there to skip that one reopen.
    shouldSkipNextMultiSelectFocusOpenRef.current = true;
    restoreInputFocus();
  };

  // Returns focus to the search input when the user moves above the first suggestion with ArrowUp.
  const restoreInputFocus = () => {
    if (isMultiselect) {
      focusMultiSelectInputToEnd();
      return;
    }
    singleSelectInputRef.current?.focus();
  };

  // Apply/Esc/single-item-click closes the popover and returns focus to the input. Skips Chips' onFocus auto-reopen so the popover stays closed; the multi flow also brings the chip strip to its end so the caret is visible.
  const closePopoverAndRestoreFocus = () => {
    setIsPopoverOpen(false);
    shouldSkipNextMultiSelectFocusOpenRef.current = true;
    restoreInputFocus();
  };

  return (
    <div
      ref={isMultiselect ? containerRef : undefined}
      className={clsx(
        'reference-input-container',
        isMultiselect
          ? 'reference-multi-container'
          : 'reference-single-container',
        chipsSelectAll && 'chips-select-all'
      )}
      onKeyDown={onContainerKeyDown}
    >
      <Popover
        isOpen={isPopoverVisible}
        positions={['bottom', 'top', 'left', 'right']}
        onClickOutside={onClickOutsidePopover}
        align="start"
        content={
          <ReferencePopover
            control={control}
            closePopover={closePopoverAndRestoreFocus}
            fetchReferenceData={() => {
              setAllDataLoaded(false);
              fetchReferenceData(
                referenceDatasets[control.datasetId],
                false,
                currentSearchValue
              );
            }}
            data={popoverData}
            // Fixes popover showing "No record found" if users filters results before initial data are loaded
            isLoading={
              (isLoading || isPopoverFetchPending) &&
              (!lazyLoading || !data?.length)
            }
            selectedItems={selectedItems}
            onItemSelect={onItemSelect}
            fetchError={fetchError}
            loadMoreData={loadMoreData}
            listRef={listRef}
            selectAllItems={selectAllItems}
            quickFilterContainerRef={containerRef}
            onLeaveTop={restoreInputFocus}
            formatLabel={formatLabel}
          />
        }
      >
        <div
          className={clsx(
            isMultiselect
              ? 'reference-multi-filter'
              : 'reference-single-filter',
            isPopoverVisible && 'reference-filter-focused',
            hasHorizontalScroll && 'reference-filter-scrollable',
            error && 'reference-filter-error',
            disabled && 'reference-filter-disabled',
            !openFullscreenReference && 'reference-filter-disabled-fullscreen',
            resolvedDecorator && 'reference-filter-with-decorator'
          )}
          onMouseDown={
            isMultiselect
              ? (event) => {
                  // A click moves the caret, so the chip highlight no longer applies.
                  clearChipFocus();
                  onMultiSelectWrapperMouseDown(event);
                }
              : undefined
          }
          onKeyDown={onWrapperKeyDown}
          onKeyDownCapture={
            isMultiselect ? onChipsNavKeyDownCapture : undefined
          }
        >
          {isMultiselect ? (
            <Chips
              placeholder={
                isResolvingDisplayedLabel ? animatedLoadingText : undefined
              }
              disabled={disabled}
              ref={chipsRef}
              inputId={chipsInputId}
              value={chipsValue}
              onChange={(e) => setChipsValue(e.value as string[])}
              onKeyDown={onMultiSelectInputKeyDown}
              onAdd={() => false}
              onRemove={onRemoveChip}
              onFocus={onMultiSelectFocus}
              onBlur={onMultiSelectBlur}
              pt={{
                input: {
                  onChange: onManualInputMultiSelect,
                  value: inputValue,
                  onClick: () => {
                    openPopover();
                    focusMultiSelectInputToEnd();
                  },
                },
              }}
            />
          ) : (
            <Input
              placeholder={
                isResolvingDisplayedLabel ? animatedLoadingText : undefined
              }
              disabled={disabled}
              inputRef={singleSelectInputRef}
              value={inputValue}
              onChange={onManualInputSingleSelect}
              onClick={onSingleSelectInputClick}
              onBlur={onSingleSelectInputBlur}
              onFocus={onFocusIn}
              error={error}
              hideErrorText
            />
          )}
          {resolvedDecorator && (
            <span
              className={REFERENCE_INPUT_DECORATOR_CLASSNAME}
              aria-hidden="true"
            >
              {resolvedDecorator}
            </span>
          )}
          {openFullscreenReference && (
            <Button
              disabled={disabled || isOpeningFullscreen}
              fill="clear"
              color="secondary"
              className="reference-popover-toggle"
              onClick={
                datasetError
                  ? () => setIsPopoverOpen(true)
                  : handleOpenFullscreenReferenceClick
              }
            >
              <IonIcon icon={menuOutline} />
            </Button>
          )}
        </div>
      </Popover>
      <InputErrorMessage error={error} />
    </div>
  );
};

export default ReferenceInput;
